import { useState, useEffect, useRef } from 'react';
import { CLINIC_NAME, CLINIC_SHORT_NAME, CLINIC_TAGLINE } from '../constants';

const ABOUT_SLIDES = [
  { src: '/images/dental-suite.jpg',          label: 'Dental Suite' },
  { src: '/images/general-consultation.jpg',  label: 'Consultation' },
  { src: '/images/cardiology-office.jpg',     label: 'Specialist Suite' },
  { src: '/images/nurse-station.jpg',         label: 'Nurse Station' },
];

const SERVICES = [
  { icon: '🩺', title: 'General Medicine', desc: 'Comprehensive primary care for all ages by experienced physicians.' },
  { icon: '🦷', title: 'Dental Care', desc: 'Modern dental suite with premium chair, imaging & consultation.' },
  { icon: '👶', title: 'Pediatrics', desc: 'Expert, gentle care for infants, children and adolescents.' },
  { icon: '❤️', title: 'Cardiology', desc: 'Advanced cardiac consultation and monitoring.' },
  { icon: '🦴', title: 'Orthopedics', desc: 'Bone, joint and muscle care from sports injuries to arthritis.' },
  { icon: '🧴', title: 'Dermatology', desc: 'Medical and cosmetic skin care by expert dermatologists.' },
  { icon: '🌸', title: 'Gynecology', desc: 'Comprehensive women’s health, screenings and consultations.' },
  { icon: '🧠', title: 'Neurology', desc: 'Diagnosis and management of nervous-system conditions.' },
  { icon: '👂', title: 'ENT', desc: 'Care for ear, nose and throat — from infections to hearing.' },
];

const PRAC_BENEFITS = [
  { icon: '🛋️', title: 'Furnished Consultation Rooms', desc: 'Examination bed, ergonomic workstation, all specialties welcome. Move in and start seeing patients immediately.' },
  { icon: '🕐', title: 'Flexible Timings', desc: 'Daily, weekly or long-term arrangements. Choose the schedule that fits your practice.' },
  { icon: '👩‍⚕️', title: 'Support Staff', desc: 'Dedicated nursing and admin team so you focus entirely on patient care.' },
  { icon: '💊', title: 'In-House Pharmacy & Lab', desc: 'Your patients get prescriptions filled and tests done on-site — same visit.' },
  { icon: '📈', title: 'Grow Your Practice', desc: 'Access a walk-in patient base, referral network and an environment that attracts quality patients.' },
  { icon: '🏢', title: 'Modern Infrastructure', desc: 'AC, lift, advanced machinery and premium interiors throughout the facility.' },
];

const PATIENT_STEPS = [
  { n: '01', title: 'Book an Appointment', desc: 'Call us or walk in. Our front-desk team matches you with the right specialist.' },
  { n: '02', title: 'Meet Your Doctor', desc: 'Visit a fully-equipped consultation room. Unhurried, thorough care.' },
  { n: '03', title: 'Get Diagnosed', desc: 'Use our on-site lab — blood work, urinalysis and more, same visit.' },
  { n: '04', title: 'Collect Your Prescription', desc: 'Our in-house pharmacy fills it immediately. Walk out fully cared for.' },
];

export default function Home({ onNav }) {
  const [tab, setTab] = useState('practitioners');
  const go = (id) => { onNav(id); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  // ── About-section carousel ────────────────────────────────────────────────
  const [aboutSlide, setAboutSlide] = useState(0);
  const [aboutPaused, setAboutPaused] = useState(false);
  const touchStartX = useRef(null);

  const goAboutSlide = (i) => {
    const len = ABOUT_SLIDES.length;
    setAboutSlide(((i % len) + len) % len);
  };

  useEffect(() => {
    if (aboutPaused) return undefined;
    const id = setInterval(
      () => setAboutSlide((s) => (s + 1) % ABOUT_SLIDES.length),
      4000,
    );
    return () => clearInterval(id);
  }, [aboutPaused]);

  const onAboutTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onAboutTouchEnd   = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) goAboutSlide(aboutSlide + (dx < 0 ? 1 : -1));
    touchStartX.current = null;
  };
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        /* ── HERO ── */
        .hero {
          min-height: 100vh; padding-top: 88px;
          position: relative; display: flex; align-items: center;
          overflow: hidden;
        }
        @media (max-width: 768px) { .hero { padding-top: 78px; } }
        @media (max-width: 480px) { .hero { padding-top: 70px; } }
        .hero-img { display: none; }
        .hero-overlay {
          position: absolute; inset: 0;
          background:
            linear-gradient(
              115deg,
              #fdfaf2 0%,
              #f1ece2 38%,
              #e9f0fa 70%,
              #fce6df 100%
            );
        }
        /* Animated colored mesh blobs */
        .hero-blob {
          position: absolute; border-radius: 50%;
          filter: blur(70px); opacity: 0.55;
          z-index: 0; pointer-events: none;
          animation: blob-float 14s ease-in-out infinite;
        }
        .hero-blob.b1 { width: 420px; height: 420px; top: -60px; left: -80px;
          background: radial-gradient(circle, #ee7960 0%, transparent 70%);
          animation-delay: 0s;
        }
        .hero-blob.b2 { width: 360px; height: 360px; top: 38%; left: 18%;
          background: radial-gradient(circle, #a8c5db 0%, transparent 70%);
          animation-duration: 18s; animation-delay: -4s;
        }
        .hero-blob.b3 { width: 480px; height: 480px; bottom: -120px; right: -60px;
          background: radial-gradient(circle, #fcc8b6 0%, transparent 70%);
          animation-duration: 16s; animation-delay: -8s;
        }
        @keyframes blob-float {
          0%, 100% { transform: translate(0,0) scale(1); }
          50%      { transform: translate(30px,-20px) scale(1.08); }
        }
        /* Subtle dot pattern */
        .hero-dots {
          position: absolute; inset: 0; z-index: 0; pointer-events: none;
          background-image: radial-gradient(rgba(30,58,95,0.10) 1px, transparent 1px);
          background-size: 22px 22px;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          opacity: 0.55;
        }
        .hero-inner {
          position: relative; z-index: 3; max-width: 1100px; margin: 0 auto;
          padding: 80px 24px; width: 100%;
        }

        /* ── BUILDING PHOTO ── */
        .hero-building {
          position: absolute;
          right: max(24px, calc((100vw - 1100px) / 2 + 8px));
          top: 110px;
          width: clamp(260px, 34vw, 420px);
          height: auto;
          object-fit: contain;
          z-index: 2;
          border-radius: 18px;
          box-shadow: 0 30px 60px -20px rgba(15,30,55,0.35),
                      0 8px 22px rgba(15,30,55,0.15);
          border: 6px solid rgba(255,255,255,0.85);
          animation: building-rise 1.1s cubic-bezier(0.16, 1, 0.3, 1) both;
          transition: transform 0.5s ease, box-shadow 0.5s ease;
        }
        .hero-building:hover {
          transform: translateY(-6px) scale(1.01);
          box-shadow: 0 38px 70px -20px rgba(15,30,55,0.45),
                      0 10px 26px rgba(15,30,55,0.2);
        }
        /* Decorative ring behind the building photo */
        .hero-building-ring {
          position: absolute;
          right: max(0px, calc((100vw - 1100px) / 2 - 16px));
          top: 70px;
          width: clamp(300px, 38vw, 480px);
          aspect-ratio: 1/1;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(30,58,95,0.10) 0%, transparent 65%);
          z-index: 1; pointer-events: none;
        }
        @keyframes building-rise {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 900px) {
          .hero-building {
            position: relative;
            right: auto; top: auto; bottom: auto;
            width: min(420px, 92%);
            max-height: none;
            margin: 24px auto 0; display: block;
          }
          .hero-building-ring { display: none; }
        }
        @media (max-width: 768px) {
          /* On phones / small tablets, hide the building photo entirely —
             no background image, no inline image. Clean text-only hero. */
          .hero { background-image: none !important; }
          .hero-building { display: none !important; }
          .hero-building-ring { display: none !important; }
        }
        .hero-badge {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 5px 13px; border-radius: 20px;
          border: 1px solid rgba(30,58,95,0.25);
          background: rgba(30,58,95,0.06);
          font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
          color: var(--terra); font-weight: 500; margin-bottom: 22px;
        }
        .hero-dot {
          width: 6px; height: 6px; background: var(--terra);
          border-radius: 50%; animation: pulse-dot 2s infinite;
        }
        .hero-title {
          font-family: var(--font-display);
          font-size: clamp(44px, 6vw, 72px);
          font-weight: 600; line-height: 1.08;
          color: var(--charcoal); letter-spacing: -0.02em;
          margin-bottom: 8px; max-width: 620px;
        }
        .hero-title em {
          font-style: italic;
          background: linear-gradient(135deg, #ee7960 0%, #f59e8a 50%, #d6604a 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          position: relative;
        }
        .hero-title em::after {
          content: '';
          position: absolute; left: 4%; right: 4%; bottom: -4px; height: 3px;
          background: linear-gradient(90deg, transparent, #ee7960, transparent);
          opacity: 0.45; border-radius: 2px;
        }
        .hero-sub {
          font-size: 16px; color: var(--terra); font-weight: 500;
          letter-spacing: 0.03em; margin-bottom: 14px;
        }
        .hero-desc {
          font-size: 15.5px; color: var(--text-mid);
          line-height: 1.75; max-width: 480px; margin-bottom: 36px;
        }
        .hero-btns { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 48px; }
        .hero-stats { display: flex; gap: 28px; flex-wrap: wrap; }
        .h-stat { display: flex; align-items: center; gap: 10px; }
        .h-stat-icon {
          width: 34px; height: 34px; border-radius: 8px;
          background: var(--terra-pale); border: 1px solid rgba(30,58,95,0.15);
          display: flex; align-items: center; justify-content: center; font-size: 15px;
        }
        .h-stat-val {
          font-family: var(--font-display); font-size: 20px; font-weight: 600;
          color: var(--charcoal); line-height: 1;
        }
        .h-stat-lbl { font-size: 11px; color: var(--text-muted); }

        /* ── STRIP ── */
        .strip {
          background: var(--charcoal); padding: 28px 0;
          border-top: 1px solid rgba(255,255,255,0.06);
        }
        .strip-inner {
          max-width: 1100px; margin: 0 auto; padding: 0 24px;
          display: grid; grid-template-columns: repeat(3,1fr); gap: 0;
        }
        .strip-item {
          text-align: center; padding: 8px 16px;
          border-right: 1px solid rgba(255,255,255,0.08);
        }
        .strip-item:last-child { border-right: none; }
        /* Coral so the big stat numbers actually read on the navy strip. */
        .strip-val {
          font-family: var(--font-display); font-size: 30px;
          font-weight: 600; color: var(--accent); display: block;
        }
        .strip-lbl {
          font-size: 11px; color: rgba(255,255,255,0.65);
          letter-spacing: 0.08em; text-transform: uppercase; margin-top: 3px;
        }

        /* ── ABOUT INTRO ── */
        .about-intro { background: var(--white); }
        .about-grid {
          display: grid; grid-template-columns: 1.1fr 0.9fr;
          gap: 56px; align-items: center;
        }
        .about-p { font-size: 15px; color: var(--text-mid); line-height: 1.8; margin-bottom: 14px; }
        /* ── About Carousel ── */
        .about-carousel {
          position: relative;
          border-radius: var(--radius);
          overflow: hidden;
          box-shadow: var(--shadow-md);
          aspect-ratio: 4/3;
          background: var(--sand-mid);
          touch-action: pan-y;
          user-select: none;
          -webkit-user-select: none;
        }
        .ac-track {
          position: absolute; inset: 0;
        }
        .ac-slide {
          position: absolute; inset: 0;
          opacity: 0; transition: opacity 0.7s ease;
          pointer-events: none;
        }
        .ac-slide.act { opacity: 1; pointer-events: auto; z-index: 1; }
        .ac-slide img {
          width: 100%; height: 100%;
          object-fit: cover; display: block;
        }
        .ac-shade {
          position: absolute; inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.32) 0%, transparent 38%);
          pointer-events: none;
        }
        .ac-label {
          position: absolute; left: 18px; bottom: 56px;
          padding: 6px 14px; border-radius: 999px;
          background: rgba(255,255,255,0.95);
          color: var(--terra);
          font-size: 12px; font-weight: 600;
          letter-spacing: 0.06em; text-transform: uppercase;
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          z-index: 2;
          max-width: calc(100% - 36px);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ac-arrow {
          position: absolute; top: 50%; transform: translateY(-50%);
          width: 42px; height: 42px;
          border-radius: 50%;
          background: rgba(255,255,255,0.88);
          border: none;
          color: var(--terra);
          font-size: 26px; line-height: 1; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; z-index: 3;
          box-shadow: 0 6px 16px rgba(0,0,0,0.12);
          transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
          padding: 0;
        }
        .ac-arrow:hover {
          background: white;
          transform: translateY(-50%) scale(1.08);
          box-shadow: 0 8px 22px rgba(0,0,0,0.18);
        }
        .ac-arrow:focus-visible {
          outline: 2px solid var(--terra);
          outline-offset: 2px;
        }
        .ac-arrow-l { left: 12px; }
        .ac-arrow-r { right: 12px; }
        .ac-dots {
          position: absolute; left: 0; right: 0; bottom: 18px;
          display: flex; justify-content: center; gap: 8px;
          z-index: 3;
        }
        .ac-dot {
          width: 9px; height: 9px; border-radius: 50%;
          background: rgba(255,255,255,0.5);
          border: 1.5px solid rgba(255,255,255,0.9);
          cursor: pointer; padding: 0;
          transition: all 0.25s ease;
        }
        .ac-dot:hover { background: rgba(255,255,255,0.85); }
        .ac-dot.act {
          background: var(--terra);
          border-color: white;
          width: 24px; border-radius: 6px;
        }
        @media (max-width: 600px) {
          .about-carousel { aspect-ratio: 4/3; }
          .ac-arrow { width: 36px; height: 36px; font-size: 22px; }
          .ac-arrow-l { left: 8px; }
          .ac-arrow-r { right: 8px; }
          .ac-label {
            left: 12px; bottom: 50px;
            font-size: 11px; padding: 5px 12px;
          }
          .ac-dot { width: 8px; height: 8px; }
          .ac-dot.act { width: 22px; }
        }

        /* ── TABS ── */
        .tabs-section { background: var(--sand); }
        .tab-bar {
          display: flex; gap: 0; margin-bottom: 40px;
          border-bottom: 2px solid var(--border);
        }
        .tab-btn {
          padding: 12px 28px; font-size: 14.5px; font-weight: 500;
          font-family: var(--font-body); cursor: pointer;
          border: none; background: none; color: var(--text-muted);
          border-bottom: 2px solid transparent; margin-bottom: -2px;
          transition: all 0.2s;
        }
        .tab-btn:hover { color: var(--charcoal); }
        .tab-btn.tab-act {
          color: var(--terra); border-bottom-color: var(--terra); font-weight: 600;
        }
        .prac-grid {
          display: grid; grid-template-columns: repeat(3,1fr); gap: 18px; margin-bottom: 32px;
        }
        .prac-card {
          background: var(--white); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 24px; transition: all 0.2s;
        }
        .prac-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); border-color: rgba(30,58,95,0.2); }
        .prac-icon {
          width: 40px; height: 40px; border-radius: 9px;
          background: var(--terra-pale); border: 1px solid rgba(30,58,95,0.15);
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; margin-bottom: 14px;
        }
        .prac-title { font-size: 14.5px; font-weight: 600; color: var(--charcoal); margin-bottom: 6px; }
        .prac-desc { font-size: 13px; color: var(--text-muted); line-height: 1.65; }

        .room-card {
          background: var(--white); border: 1px solid var(--border);
          border-radius: var(--radius); overflow: hidden;
          display: grid; grid-template-columns: 1fr 1fr;
          margin-bottom: 32px;
        }
        .room-img { aspect-ratio: 4/3; }
        .room-img img { width: 100%; height: 100%; object-fit: cover; }
        .room-content { padding: 32px 28px; display: flex; flex-direction: column; justify-content: center; }
        .room-title {
          font-family: var(--font-display); font-size: 26px; font-weight: 600;
          color: var(--charcoal); margin-bottom: 10px;
        }
        .room-desc { font-size: 14px; color: var(--text-muted); line-height: 1.75; margin-bottom: 20px; }
        .room-tags { display: flex; flex-wrap: wrap; gap: 8px; }
        .room-tag {
          padding: 5px 12px; border-radius: 4px; font-size: 12px;
          background: var(--terra-pale); color: var(--terra);
          border: 1px solid rgba(30,58,95,0.15);
        }

        .pat-steps {
          display: grid; grid-template-columns: repeat(2,1fr); gap: 18px; margin-bottom: 32px;
        }
        .pat-step {
          background: var(--white); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 24px; display: flex; gap: 16px;
        }
        .step-n {
          font-family: var(--font-display); font-size: 34px; font-weight: 600;
          color: var(--terra); opacity: 0.25; line-height: 1; flex-shrink: 0; min-width: 44px;
        }
        .step-title { font-size: 14.5px; font-weight: 600; color: var(--charcoal); margin-bottom: 6px; }
        .step-desc { font-size: 13px; color: var(--text-muted); line-height: 1.65; }

        /* ── SERVICES ── */
        .svc-section { background: var(--white); }
        .svc-grid {
          display: grid; grid-template-columns: repeat(3,1fr); gap: 18px; margin-top: 36px;
        }
        .svc-card {
          position: relative;
          background: var(--sand); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 26px 24px;
          transition: all 0.25s ease; cursor: default;
          overflow: hidden;
        }
        .svc-card::before {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(30,58,95,0.05) 0%, transparent 60%);
          opacity: 0; transition: opacity 0.3s ease;
        }
        .svc-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 18px 40px -12px rgba(15,30,55,0.18);
          border-color: rgba(30,58,95,0.28);
          background: var(--white);
        }
        .svc-card:hover::before { opacity: 1; }
        .svc-icon {
          position: relative;
          width: 52px; height: 52px;
          border-radius: 14px;
          background: linear-gradient(135deg, #fce6df 0%, #f5b8a8 100%);
          border: 1px solid rgba(30,58,95,0.18);
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 26px;
          margin-bottom: 14px;
          box-shadow: 0 6px 14px -6px rgba(30,58,95,0.35);
          transition: transform 0.3s ease;
        }
        .svc-card:hover .svc-icon { transform: scale(1.08) rotate(-4deg); }
        .svc-title { font-size: 15px; font-weight: 600; color: var(--charcoal); margin-bottom: 6px; }
        .svc-desc { font-size: 13px; color: var(--text-muted); line-height: 1.65; }

        /* ── WHY ── */
        .why-section { background: var(--sand-mid); }
        .why-grid {
          display: grid; grid-template-columns: repeat(2,1fr); gap: 18px; margin-top: 36px;
        }
        .why-card {
          background: var(--white); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 28px; display: flex; gap: 16px;
          align-items: flex-start; transition: all 0.2s;
        }
        .why-card:hover { box-shadow: var(--shadow-md); border-color: rgba(30,58,95,0.2); }
        .why-icon {
          width: 44px; height: 44px; border-radius: 10px;
          background: var(--terra-pale); border: 1px solid rgba(30,58,95,0.15);
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0;
        }
        .why-title { font-size: 15px; font-weight: 600; color: var(--charcoal); margin-bottom: 6px; }
        .why-desc { font-size: 13px; color: var(--text-muted); line-height: 1.65; }

        /* ── CTA BANNER ── */
        .cta-banner {
          background: var(--terra); padding: 56px 0; position: relative; overflow: hidden;
        }
        .cta-banner::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(circle at 80% 50%, rgba(255,255,255,0.06) 0%, transparent 60%);
        }
        .cta-inner {
          max-width: 1100px; margin: 0 auto; padding: 0 24px;
          display: flex; justify-content: space-between; align-items: center;
          gap: 28px; flex-wrap: wrap; position: relative;
        }
        .cta-title {
          font-family: var(--font-display); font-size: clamp(24px,3.5vw,36px);
          font-weight: 600; color: white; margin-bottom: 6px;
        }
        .cta-sub { font-size: 15px; color: rgba(255,255,255,0.75); }
        .cta-btn {
          padding: 14px 32px; border-radius: var(--radius-sm);
          background: white; color: var(--terra);
          font-size: 14px; font-weight: 600; font-family: var(--font-body);
          border: none; cursor: pointer; flex-shrink: 0;
          transition: all 0.2s; white-space: nowrap;
        }
        .cta-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.15); }

        @media (max-width: 900px) {
          .hero-img { opacity: 0.35; }
          .hero-overlay { background: rgba(253,250,242,0.88); }
          .about-grid { grid-template-columns: 1fr; gap: 28px; }
          .prac-grid { grid-template-columns: repeat(2,1fr); }
          .room-card { grid-template-columns: 1fr; }
          .svc-grid { grid-template-columns: repeat(2,1fr); }
          .why-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 600px) {
          .hero-title { font-size: 38px; }
          .hero-btns { flex-direction: column; }
          .btn-terra, .btn-outline { width: 100%; justify-content: center; }
          .strip-inner { grid-template-columns: 1fr; }
          .strip-item { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.08); padding: 12px 0; }
          .strip-item:last-child { border-bottom: none; }
          .prac-grid { grid-template-columns: 1fr; }
          .pat-steps { grid-template-columns: 1fr; }
          .svc-grid { grid-template-columns: 1fr; }
          .tab-btn { padding: 10px 16px; font-size: 13px; }
        }
      `}</style>

      {/* HERO */}
      <section className="hero">
        <div className="hero-overlay" />
        <div className="hero-blob b1" />
        <div className="hero-blob b2" />
        <div className="hero-blob b3" />
        <div className="hero-dots" />
        <div className="hero-building-ring" />

        {/* Clinic building photo (replace /public/images/clinic-building.jpg) */}
        <img
          className="hero-building"
          src="/images/clinic-building.jpg"
          alt={`${CLINIC_NAME} — clinic building exterior`}
        />


        <div className="hero-inner">
          <div className="hero-badge">
            <span className="hero-dot" />
            Launching 2026 · Siddipet
          </div>
          <h1 className="hero-title">
            Where Healthcare<br />Meets <em>Excellence.</em>
          </h1>
          <p className="hero-sub">{CLINIC_NAME} — {CLINIC_TAGLINE}</p>
          <p className="hero-desc">
            A premium polyclinic & medical co-working space. Fully-equipped consultation rooms,
            all specialties welcome — trusted care under one roof.
          </p>
          <div className="hero-btns">
            <button className="btn-terra" onClick={() => go('practitioners')}>Join as Practitioner →</button>
            <button className="btn-outline" onClick={() => go('patients')}>For Patients</button>
          </div>
          <div className="hero-stats">
            <div className="h-stat">
              <div className="h-stat-icon">🏥</div>
              <div>
                <div className="h-stat-val">10+</div>
                <div className="h-stat-lbl">Consult Rooms</div>
              </div>
            </div>
            <div className="h-stat">
              <div className="h-stat-icon">🩺</div>
              <div>
                <div className="h-stat-val">All</div>
                <div className="h-stat-lbl">Specialties Welcome</div>
              </div>
            </div>
            <div className="h-stat">
              <div className="h-stat-icon">📍</div>
              <div>
                <div className="h-stat-val">Siddipet</div>
                <div className="h-stat-lbl">Telangana</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <div className="strip">
        <div className="strip-inner">
          <div className="strip-item"><span className="strip-val">10+</span><div className="strip-lbl">Consult Rooms</div></div>
          <div className="strip-item"><span className="strip-val">All</span><div className="strip-lbl">Specialties Welcome</div></div>
          <div className="strip-item"><span className="strip-val">A–Z</span><div className="strip-lbl">Care Under One Roof</div></div>
        </div>
      </div>

      {/* ABOUT INTRO */}
      <section className="section about-intro">
        <div className="container">
          <div className="about-grid">
            <div>
              <span className="eyebrow">About {CLINIC_NAME}</span>
              <h2 className="section-title" style={{marginBottom:16}}>A new standard<br />in healthcare</h2>
              <p className="about-p">
                {CLINIC_NAME} is a first-of-its-kind premium facility in Siddipet — offering fully-equipped,
                beautifully furnished OP consultation rooms with complete clinical & diagnostic support.
              </p>
              <p className="about-p">
                Purpose-built for healthcare professionals who demand the finest environment for their patients
                and their practice. All specialties welcome. Care under one roof.
              </p>
              <button className="btn-terra" style={{marginTop:8}} onClick={() => go('about')}>
                Our Story →
              </button>
            </div>
            <div
              className="about-carousel"
              onMouseEnter={() => setAboutPaused(true)}
              onMouseLeave={() => setAboutPaused(false)}
              onTouchStart={onAboutTouchStart}
              onTouchEnd={onAboutTouchEnd}
              role="region"
              aria-roledescription="carousel"
              aria-label="Inside Doctors Plaza - Polyclinic"
            >
              <div className="ac-track">
                {ABOUT_SLIDES.map((s, i) => (
                  <div
                    key={s.src}
                    className={`ac-slide ${i === aboutSlide ? 'act' : ''}`}
                    aria-hidden={i !== aboutSlide}
                  >
                    <img src={s.src} alt={s.label} loading={i === 0 ? 'eager' : 'lazy'} />
                    <div className="ac-shade" />
                    <span className="ac-label">{s.label}</span>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="ac-arrow ac-arrow-l"
                onClick={() => goAboutSlide(aboutSlide - 1)}
                aria-label="Previous image"
              >‹</button>
              <button
                type="button"
                className="ac-arrow ac-arrow-r"
                onClick={() => goAboutSlide(aboutSlide + 1)}
                aria-label="Next image"
              >›</button>

              <div className="ac-dots" role="tablist">
                {ABOUT_SLIDES.map((s, i) => (
                  <button
                    key={s.src}
                    type="button"
                    className={`ac-dot ${i === aboutSlide ? 'act' : ''}`}
                    onClick={() => goAboutSlide(i)}
                    aria-label={`Show image ${i + 1}: ${s.label}`}
                    aria-selected={i === aboutSlide}
                    role="tab"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TABBED: PRACTITIONERS / PATIENTS */}
      <section className="section tabs-section">
        <div className="container">
          <div className="tab-bar">
            <button className={`tab-btn ${tab === 'practitioners' ? 'tab-act' : ''}`} onClick={() => setTab('practitioners')}>
              👨‍⚕️ For Practitioners
            </button>
            <button className={`tab-btn ${tab === 'patients' ? 'tab-act' : ''}`} onClick={() => setTab('patients')}>
              👨‍👩‍👧 For Patients
            </button>
          </div>

          {tab === 'practitioners' && (
            <>
              <span className="eyebrow">Why Join {CLINIC_SHORT_NAME}</span>
              <h2 className="section-title" style={{marginBottom:32}}>A clinic you'll be proud to practise from</h2>
              <div className="room-card">
                <div className="room-img">
                  <img src="/images/consultation-room.jpg" alt="Premium consultation room" />
                </div>
                <div className="room-content">
                  <div className="room-title">Premium Consultation Rooms</div>
                  <p className="room-desc">Elegantly designed, fully-furnished suites — ready from day one. Everything you need to deliver exceptional care.</p>
                  <div className="room-tags">
                    <span className="room-tag">Examination Bed</span>
                    <span className="room-tag">Ergonomic Workstation</span>
                    <span className="room-tag">All Specialties</span>
                    <span className="room-tag">Flexible Timings</span>
                    <span className="room-tag">Support Staff</span>
                  </div>
                </div>
              </div>
              <div className="prac-grid">
                {PRAC_BENEFITS.map(b => (
                  <div key={b.title} className="prac-card">
                    <div className="prac-icon">{b.icon}</div>
                    <div className="prac-title">{b.title}</div>
                    <div className="prac-desc">{b.desc}</div>
                  </div>
                ))}
              </div>
              <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
                <button className="btn-terra" onClick={() => go('contact')}>Join as Practitioner →</button>
                <button className="btn-outline" onClick={() => go('practitioners')}>Learn More</button>
              </div>
            </>
          )}

          {tab === 'patients' && (
            <>
              <span className="eyebrow">Your Visit</span>
              <h2 className="section-title" style={{marginBottom:32}}>Care that puts you first</h2>
              <div className="pat-steps">
                {PATIENT_STEPS.map(s => (
                  <div key={s.n} className="pat-step">
                    <div className="step-n">{s.n}</div>
                    <div>
                      <div className="step-title">{s.title}</div>
                      <div className="step-desc">{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <button className="btn-terra" onClick={() => go('contact')}>📅 Book Appointment</button>
            </>
          )}
        </div>
      </section>

      {/* SERVICES */}
      <section className="section svc-section">
        <div className="container">
          <span className="eyebrow">Our Services</span>
          <h2 className="section-title">Comprehensive care<br />under one roof</h2>
          <p className="section-desc">From routine check-ups to specialist consultations — every service you need, right here in Siddipet.</p>
          <div className="svc-grid">
            {SERVICES.map(s => (
              <div key={s.title} className="svc-card">
                <span className="svc-icon">{s.icon}</span>
                <div className="svc-title">{s.title}</div>
                <div className="svc-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY OMNI */}
      <section className="section why-section">
        <div className="container">
          <span className="eyebrow">Why {CLINIC_NAME}</span>
          <h2 className="section-title">A new standard in healthcare</h2>
          <div className="why-grid">
            {[
              { icon: '🛋️', title: 'Premium Consultation Rooms', desc: 'Fully-furnished, elegantly designed suites with everything a practitioner needs from day one.' },
              { icon: '🩺', title: 'All Specialties Welcome', desc: 'General medicine, dental, cardiology, orthopedics and more — all under one roof in Siddipet.' },
              { icon: '🔬', title: 'In-House Lab & Pharmacy', desc: 'On-site diagnostics and a licensed pharmacy so patients never need to step outside.' },
              { icon: '👩‍⚕️', title: 'Full Support Staff', desc: 'Dedicated nursing and administrative team supporting both practitioners and patients.' },
            ].map(w => (
              <div key={w.title} className="why-card">
                <div className="why-icon">{w.icon}</div>
                <div>
                  <div className="why-title">{w.title}</div>
                  <div className="why-desc">{w.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <div className="cta-banner">
        <div className="cta-inner">
          <div>
            <div className="cta-title">Ready to be part of {CLINIC_SHORT_NAME}?</div>
            <div className="cta-sub">Launching 2026 · Register your interest today.</div>
          </div>
          <button className="cta-btn" onClick={() => go('contact')}>Get in Touch</button>
        </div>
      </div>
    </>
  );
}
